import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { useSeo } from "@/lib/useSeo";
import { PAGE_SEO } from "@/data/pageSeo";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { getAttribution } from "@/lib/utm";
import { toast } from "sonner";
import { Loader2, ArrowLeft, ArrowRight, Check } from "lucide-react";
import { format } from "date-fns";
import { Step1ServiceSelect, Step2TripDetails, Step3ContactInfo, Step4Review } from "@/components/booking/steps";
import { EstimateSidebar } from "@/components/booking/EstimateSidebar";

export default function Booking() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  useSeo({ ...(PAGE_SEO.book[lang] || PAGE_SEO.book.en), path: PAGE_SEO.book.path, image: PAGE_SEO.book.image, lang });
  const [params] = useSearchParams();
  const location = useLocation();
  // Rich trip data handed off from the homepage hero widget (coords, place ids, airport flags).
  // Prefer router state (survives SPA back/forward); fall back to sessionStorage on hard reload,
  // but only trust it when its addresses still match the current URL query (prevents stale injection).
  const handoff = (() => {
    if (location.state?.handoff) return location.state.handoff;
    try {
      const raw = sessionStorage.getItem("autobus_hero_handoff");
      if (!raw) return null;
      const h = JSON.parse(raw);
      const pOk = !h.pickup || h.pickup.address === params.get("pickup");
      const dOk = !h.dropoff || h.dropoff.address === params.get("dropoff");
      return pOk && dOk ? h : null;
    } catch {
      return null;
    }
  })();
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState({ services: [], lead_sources: [] });
  const [estimate, setEstimate] = useState(null);
  const [route, setRoute] = useState(null);
  const [routeGeo, setRouteGeo] = useState(null);
  const [date, setDate] = useState(() => {
    const d = params.get("date");
    if (d) { const parsed = new Date(d + "T00:00:00"); if (!isNaN(parsed)) return parsed; }
    return null;
  });
  const [submitting, setSubmitting] = useState(false);
  const [availVehicles, setAvailVehicles] = useState([]);
  const [decision, setDecision] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [hold, setHold] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);

  const [form, setForm] = useState({
    service_type: params.get("service") || "",
    package_id: "",
    name: "", email: "", phone: "",
    pickup_location: handoff?.pickup?.address || params.get("pickup") || "",
    dropoff_location: handoff?.dropoff?.address || params.get("dropoff") || "",
    pickup_coords: handoff?.pickup?.coords || null,
    dropoff_coords: handoff?.dropoff?.coords || null,
    pickup_place: handoff?.pickup || null,
    dropoff_place: handoff?.dropoff || null,
    pickup_is_airport: handoff?.pickup?.is_airport || false,
    dropoff_is_airport: handoff?.dropoff?.is_airport || false,
    time: params.get("time") || "", passengers: Number(params.get("passengers")) || 2, hours: 3,
    trip_type: params.get("trip_type") || "one_way", luggage: 0, flight_number: "", vehicle_id: "",
    occasion: "", special_requests: "", lead_source: "", extra_stops: [],
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isTransit = form.service_type === "transit_bus";

  useEffect(() => {
    api.get("/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
        phone: f.phone || user.phone || "",
        lead_source: f.lead_source || user.lead_source || "",
      }));
    }
  }, [user]);

  useEffect(() => {
    if (!form.service_type) return;
    if (isTransit && !form.package_id) { setEstimate(null); return; }
    api.post("/bookings/estimate", {
      service_type: form.service_type,
      package_id: form.package_id || null,
      hours: Number(form.hours),
      passengers: Number(form.passengers),
    }).then(({ data }) => setEstimate(data)).catch(() => {});
  }, [form.service_type, form.hours, form.passengers, form.package_id, isTransit]);

  const selectedService = config.services.find((s) => s.id === form.service_type);

  const pickupISO = useCallback(
    () => (date && form.time ? `${format(date, "yyyy-MM-dd")}T${form.time}:00` : null),
    [date, form.time]
  );
  const kmFromCoords = useCallback(() => {
    const a = form.pickup_coords, b = form.dropoff_coords;
    if (!a || !b) return 0;
    const R = 6371, dLat = ((b.lat - a.lat) * Math.PI) / 180, dLng = ((b.lng - a.lng) * Math.PI) / 180;
    const x = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  }, [form.pickup_coords, form.dropoff_coords]);

  // Availability + instant/quote decision (non-transit) once trip details are known
  useEffect(() => {
    if (isTransit || !form.service_type || !date || !form.time) { setAvailVehicles([]); return; }
    api.post("/availability", {
      service_type: form.service_type, pickup_datetime: pickupISO(),
      hours: Number(form.hours), passengers: Number(form.passengers), luggage: Number(form.luggage),
    }).then(({ data }) => setAvailVehicles(data.vehicles)).catch(() => setAvailVehicles([]));
    api.post("/bookings/decision", {
      service_type: form.service_type, trip_type: form.trip_type,
      passengers: Number(form.passengers), stops: (form.extra_stops || []).length, pickup_datetime: pickupISO(),
      pickup_coords: form.pickup_coords, dropoff_coords: form.dropoff_coords,
      route_km: route ? Number(route.km) : null, vehicle_id: form.vehicle_id || null,
    }).then(({ data }) => setDecision(data)).catch(() => setDecision(null));
  }, [form.service_type, form.trip_type, form.passengers, form.hours, form.luggage, form.vehicle_id, form.extra_stops, date, form.time, form.pickup_coords, form.dropoff_coords, route, isTransit, pickupISO]);

  // Engine price breakdown for the selected vehicle
  useEffect(() => {
    if (isTransit || !form.vehicle_id) { setBreakdown(null); return; }
    api.post("/pricing/vehicle", {
      vehicle_id: form.vehicle_id, hours: Number(form.hours),
      km: route ? Number(route.km) : kmFromCoords(), stops: 0,
    }).then(({ data }) => setBreakdown(data)).catch(() => setBreakdown(null));
  }, [form.vehicle_id, form.hours, route, isTransit, kmFromCoords]);

  // 15-minute hold countdown on the review step (instant bookings with a chosen vehicle)
  useEffect(() => {
    if (!hold) return;
    const iv = setInterval(() => {
      const secs = Math.max(0, Math.floor((new Date(hold.expires_at) - new Date()) / 1000));
      setTimeLeft(secs);
      if (secs <= 0) clearInterval(iv);
    }, 1000);
    return () => clearInterval(iv);
  }, [hold]);

  const isQuoteMode = !isTransit && decision?.mode === "quote";

  // Route geometry (all non-transit) for the map preview + distance/time (keyless OSRM), incl. stops
  useEffect(() => {
    const pc = form.pickup_coords, dc = form.dropoff_coords;
    if (isTransit || !pc || !dc) { setRoute(null); setRouteGeo(null); return; }
    const stopCoords = (form.extra_stops || []).filter((s) => s.place).map((s) => `${s.place.longitude},${s.place.latitude}`);
    const waypoints = [`${pc.lng},${pc.lat}`, ...stopCoords, `${dc.lng},${dc.lat}`].join(";");
    let cancelled = false;
    fetch(`https://router.project-osrm.org/route/v1/driving/${waypoints}?overview=full&geometries=geojson`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        const rt = d.routes?.[0];
        if (rt) {
          setRoute({ km: (rt.distance / 1000).toFixed(1), min: Math.round(rt.duration / 60) });
          setRouteGeo(rt.geometry?.coordinates || null);
        } else { setRoute(null); setRouteGeo(null); }
      })
      .catch(() => { if (!cancelled) { setRoute(null); setRouteGeo(null); } });
    return () => { cancelled = true; };
  }, [isTransit, form.pickup_coords, form.dropoff_coords, form.extra_stops]);

  const goToReview = async () => {
    // create a 15-min hold when moving to review for an instant vehicle booking
    if (!isTransit && !isQuoteMode && form.vehicle_id && !hold) {
      try {
        const { data } = await api.post("/holds", {
          vehicle_id: form.vehicle_id, pickup_datetime: pickupISO(), hours: Number(form.hours),
        });
        setHold(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Vehicle no longer available");
        return false;
      }
    }
    return true;
  };

  const canNext = () => {
    if (step === 1) return !!form.service_type;
    if (step === 2) {
      // Require a VALID selected location (coords), not just free text
      if (isTransit) return form.package_id && form.pickup_coords && date && form.time;
      const base = form.pickup_coords && form.dropoff_coords && date && form.time;
      const stopsValid = (form.extra_stops || []).every((s) => !s.location || s.place);
      return base && stopsValid && (isQuoteMode || form.vehicle_id);
    }
    if (step === 3) return form.name && form.email && form.phone;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        vehicle: isTransit ? estimate?.vehicle : selectedService?.vehicle,
        vehicle_id: isTransit ? null : (form.vehicle_id || null),
        passengers: Number(form.passengers),
        hours: Number(form.hours),
        luggage: Number(form.luggage),
        stops: (form.extra_stops || []).length,
        extra_stops: (form.extra_stops || []).filter((s) => s.place).map((s) => s.place),
        route_km: route ? Number(route.km) : null,
        hold_id: hold?.hold_id || null,
        request_quote: isQuoteMode,
        language: lang,
        date: format(date, "yyyy-MM-dd"),
        ...(getAttribution() || {}),
      };
      const { data } = await api.post("/bookings", payload);
      toast.success("Reservation created");
      navigate(`/booking/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [t("booking.step.service"), t("booking.step.trip"), t("booking.step.info"), t("booking.step.review")];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-32 pb-24">
        <button onClick={() => navigate("/")} className="inline-flex items-center text-sm text-muted-foreground hover:text-white mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" /> {t("booking.back")}
        </button>
        <h1 className="font-serif font-light text-4xl sm:text-5xl text-white mb-3">{t("booking.title")}</h1>
        <p className="text-muted-foreground mb-10">{t("booking.subtitle")}</p>

        {/* Stepper */}
        <div className="flex items-center gap-2 sm:gap-4 mb-12">
          {steps.map((label, i) => {
            const n = i + 1;
            const active = step === n;
            const done = step > n;
            return (
              <div key={label} className="flex items-center gap-2 sm:gap-4 flex-1">
                <div className="flex items-center gap-3">
                  <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm border transition-all ${
                    active ? "bg-white text-black border-white" :
                    done ? "bg-white/10 text-white border-white/30" :
                    "bg-transparent text-muted-foreground border-white/15"}`}>
                    {done ? <Check className="h-4 w-4" /> : n}
                  </div>
                  <span className={`hidden sm:block text-sm ${active ? "text-white" : "text-muted-foreground"}`}>{label}</span>
                </div>
                {i < steps.length - 1 && <div className="h-px bg-white/10 flex-1" />}
              </div>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            {step === 1 && <Step1ServiceSelect config={config} form={form} set={set} t={t} />}
            {step === 2 && (
              <Step2TripDetails
                isTransit={isTransit} config={config} form={form} set={set}
                date={date} setDate={setDate} decision={decision} isQuoteMode={isQuoteMode}
                availVehicles={availVehicles} t={t}
              />
            )}
            {step === 3 && <Step3ContactInfo config={config} form={form} set={set} t={t} />}
            {step === 4 && (
              <Step4Review
                isTransit={isTransit} config={config} form={form} selectedService={selectedService}
                availVehicles={availVehicles} date={date} estimate={estimate} routeGeo={routeGeo} route={route} t={t}
              />
            )}

            {/* Nav buttons */}
            <div className="flex justify-between mt-10">
              <Button data-testid="booking-back-btn" variant="ghost"
                className="text-white hover:bg-white/10 rounded-full"
                onClick={() => step === 1 ? navigate("/") : setStep(step - 1)}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
              {step < 4 ? (
                <Button data-testid="booking-next-btn" disabled={!canNext()}
                  className="rounded-full bg-white text-black hover:bg-white/85 px-8"
                  onClick={async () => {
                    if (step === 3) { const ok = await goToReview(); if (!ok) return; }
                    setStep(step + 1);
                  }}>
                  {t("booking.continue")} <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button data-testid="booking-submit-btn" disabled={submitting}
                  className="rounded-full bg-white text-black hover:bg-white/85 px-8" onClick={submit}>
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                    <>{(isTransit && (!estimate || estimate.quote_required)) || isQuoteMode ? t("booking.requestQuote") : t("booking.confirmPay")} <ArrowRight className="h-4 w-4 ml-2" /></>
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Estimate sidebar */}
          <div className="lg:col-span-1">
            <EstimateSidebar
              isTransit={isTransit} selectedService={selectedService} estimate={estimate} form={form}
              config={config} route={route} breakdown={breakdown} isQuoteMode={isQuoteMode}
              hold={hold} timeLeft={timeLeft} t={t}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
